import {highlightLocation, markedLocation, trailDirection} from '@gaia/icons';
import {loadImage} from '@gaia/utils/gaia/getMapboxImages';
import {useEffect} from 'react';

const highlightLocationId = 'highlightLocationIcon';
const trailDirectionId = 'trailDirectionIcon';
const markedLocationId = 'markedLocationIcon';

export const preload = {
    [highlightLocationId]: highlightLocation,
    [trailDirectionId]: trailDirection,
    [markedLocationId]: markedLocation,
};

export function usePreloadMapboxImages(
    map: MapboxMap | undefined,
    sprite: GaiaMapboxStyle['sprite']
) {
    useEffect(() => {
        // we need to wait for the sprite to load before trying to preload images
        if (!map || !sprite) return;

        const controller = new AbortController();

        preloadImages(map, controller.signal);

        return () => {
            controller.abort();
            if (map.hasImage(highlightLocationId)) map.removeImage(highlightLocationId);
            if (map.hasImage(trailDirectionId)) map.removeImage(trailDirectionId);
            if (map.hasImage(markedLocationId)) map.removeImage(markedLocationId);
        };
    }, [map, sprite]);
}

function preloadImages(map: MapboxMap, signal: AbortSignal) {
    // These keys are significant. They correspond to ids that Mapbox knows about from the sprite
    // sheet, i.e. the map would fires a `styleimagemissing` event for them with these ids if not
    // preloaded.
    Object.entries(preload).forEach(([id, imgSrc]) =>
        loadImage(imgSrc).then((img) => {
            if (signal.aborted) return;

            // Normally, `onStyleImageMissing.ts` is only called for map images that are handled
            // dynamically, like waypoints. However, when the map is first loaded, there might be a
            // race while these images are preloading. If the map tries to render an image before it
            // has been preloaded here, in development, it will be given a label with the name of the
            // missing image to display instead.
            //
            // Once the real image finishes loading, we want to replace the label with the real image.
            if (map.hasImage(id)) map.removeImage(id);

            map.addImage(id, img, {pixelRatio: 2});
        }, console.error)
    );
}
